export type TradeDirection = 'BUY' | 'SELL';
export type TradeStatus = 'OPEN' | 'WIN' | 'LOSS' | 'BE' | 'PENDING' | 'NOT_TRIGGERED';
export type MarketType =
  | 'FOREX' | 'CRYPTO' | 'STOCKS' | 'INDICES' | 'COMMODITIES';
export type Timeframe =
  | 'M1' | 'M5' | 'M15' | 'M30' | 'H1' | 'H4' | 'D1';

export interface Comment {
  id: string;
  author: string;
  authorInitial: string;
  authorAvatarUrl?: string | null;
  authorId?: string;
  authorVerificationTier?: string | null;
  content: string;
  imageUrl?: string | null;
  createdAt: string;
  parentId?: string | null;
  likeCount?: number;
  isLiked?: boolean;
  replies?: Comment[];
  isAiAnalysis?: boolean;
}

// One public ledger entry for an SL/TP change made while the signal was live
// (TradeIdeaRevision — see supabase/migrations/20260902120000_tradeidea_level_revisions.sql).
export interface TradeRevision {
  id: string;
  createdAt: string;
  changes: { field: 'stopLoss' | 'takeProfit1' | 'takeProfit2' | 'takeProfit3' | string; from: number | null; to: number | null }[];
  note?: string | null;
}

export interface TradeCardData {
  id: string;
  ticker: string;
  market: MarketType;
  direction: TradeDirection;
  timeframe: Timeframe;
  status: TradeStatus;
  ideaType?: 'SIGNAL' | 'ANALYSIS';
  entryPrice: number;
  stopLoss: number;
  takeProfit1: number;
  takeProfit2?: number;
  takeProfit3?: number;
  caption: string;
  imageUrl?: string;
  chartImage?: string;
  thumbnailUrl?: string;
  externalUrl?: string;
  /** Locked chart framing so the screenshot + live chart share an identical date+price window. */
  chartWindow?: { fromTs: number; toTs: number; priceMin: number; priceMax: number };
  createdAt: string;
  editedAt?: string | null;
  // Public SL/TP change ledger (newest first). Feed cards carry only the count.
  revisions?: TradeRevision[];
  revisionCount?: number;
  // Signal lifecycle: T0 = createdAt (publish), T1 = activatedAt (entry first reached),
  // T2 = exitedAt (SL/TP hit). entryType MARKET activates at publish; LIMIT when filled.
  entryType?: 'MARKET' | 'LIMIT';
  // Demo-position order mechanics surfaced by /api/trades (see route.ts):
  // pending LIMIT/STOP orders carry a trigger and, once filled, an anchored entry.
  orderType?: 'MARKET' | 'LIMIT' | 'STOP';
  triggerPrice?: number | null;
  fillEntry?: number | null;
  activatedAt?: string | null;
  exitedAt?: string | null;
  outcomePrice?: number | null;
  viewCount?: number;
  author: {
    username: string;
    displayName: string;
    initial: string;
    isFollowing: boolean;
    avatarUrl?: string;
    userId?: string;
    winRate?: number | null;
    totalTrades?: number | null;
    avgRR?: number | null;
    performanceScore?: number | null;
    verificationTier?: string | null;
  };
  likeCount: number;
  commentCount: number;
  isLiked: boolean;
  isSaved: boolean;
  comments: Comment[];
  reactions?: {
    bullish: number;
    bearish: number;
    fire: number;
    userReaction?: 'bullish' | 'bearish' | 'fire' | null;
  };
  is_demo?: boolean;
}

// design_handoff_showmetrade palette — FOREX matches the mockup's explicit blue market
// chip exactly; the rest are assigned from the same light-theme token set for distinctness.
export const marketColors: Record<MarketType, { bg: string; text: string; border: string }> = {
  FOREX:       { bg: '#e7ecfa', text: '#3e5fbf', border: 'transparent' },
  CRYPTO:      { bg: '#f7eeda', text: '#b07c1f', border: 'transparent' },
  STOCKS:      { bg: '#e1f3e8', text: '#0e8a4d', border: 'transparent' },
  INDICES:     { bg: '#e7ecfa', text: '#3e5fbf', border: 'transparent' },
  COMMODITIES: { bg: '#f7eeda', text: '#b07c1f', border: 'transparent' },
};

export const timeframeLabels: Record<Timeframe, string> = {
  M1: '1m', M5: '5m', M15: '15m', M30: '30m',
  H1: '1H', H4: '4H', D1: 'D1',
};

export function getRR(entry: number, sl: number, tp: number, direction: TradeDirection): string {
  const risk = direction === 'BUY' ? entry - sl : sl - entry;
  const reward = direction === 'BUY' ? tp - entry : entry - tp;
  if (risk <= 0) return '—';
  const rr = reward / risk;
  if (rr <= 0) return '—';
  return rr.toFixed(2);
}

export function formatPrice(price: number): string {
  return price.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 5,
  });
}

export { timeAgoShort as formatTime } from '@/lib/dates';

export function buildCommentTree(flat: Comment[]): Comment[] {
  const map = new Map<string, Comment & { replies: Comment[] }>();
  const roots: (Comment & { replies: Comment[] })[] = [];

  flat.forEach((c) => map.set(c.id, { ...c, replies: [] }));
  flat.forEach((c) => {
    const node = map.get(c.id)!;
    if (c.parentId && map.has(c.parentId)) {
      map.get(c.parentId)!.replies.push(node);
    } else {
      roots.push(node);
    }
  });
  return roots;
}
